import type AwcPopoverElement from '@ambiki/impulse-view-components/dist/elements/popover';
import { Controller } from '@hotwired/stimulus';
import { chunks } from 'helpers/array';
import { eachDayOfInterval } from 'helpers/date';
import dayjs, { Dayjs } from 'lib/dayjs';
import { CalendarPicker } from 'schedules/types';

export default class extends Controller {
  static targets = ['monthText', 'monthGrid', 'rowTemplate', 'btnTemplate', 'row'];
  static values = { datetime: String };

  activeDate = dayjs();
  private controller = new AbortController();

  initialize() {
    this.activeDate = dayjs(this.datetimeValue);
  }

  connect(): void {
    this.publishMonthChanges();
    this.popover?.addEventListener('awc-popover:hidden', this.reset.bind(this), { signal: this.controller.signal });
  }

  disconnect(): void {
    this.controller.abort();
  }

  datetimeValueChanged(value: string) {
    this.activeDate = dayjs(value);
    this.publishMonthChanges();
    this.selectCurrentWeek();
  }

  commit(event: Event) {
    const { target } = event;
    if (!(target instanceof HTMLElement)) return;
    const row = target.closest('[value]');
    const value = row?.getAttribute('value');
    if (!value) return;

    const date = new Date(value);
    const changeEvent = this.dispatch('change', { detail: { value: date }, bubbles: true, cancelable: true });
    if (!changeEvent) return;

    this.datetimeValue = dayjs.utc(date).format();
  }

  yearChange(event: CustomEvent<CalendarPicker>) {
    this.activeDate = dayjs(event.detail.value);
    this.publishMonthChanges();
  }

  previousMonth() {
    this.activeDate = this.activeDate.subtract(1, 'month');
    this.publishMonthChanges();
    this.updateMonthPickerValue(this.activeDate.startOf('month'));
  }

  nextMonth() {
    this.activeDate = this.activeDate.add(1, 'month');
    this.publishMonthChanges();
    this.updateMonthPickerValue(this.activeDate.startOf('month'));
  }

  reset() {
    if (this.activeDate.isSame(dayjs(this.datetimeValue), 'day')) return;

    this.activeDate = dayjs(this.datetimeValue);
    this.updateMonthPickerValue(this.activeDate);
    this.publishMonthChanges();
  }

  private publishMonthChanges() {
    this.monthTextTarget.innerText = this.activeDate.format('MMMM YYYY');
    this.populateCalendar();
  }

  private selectCurrentWeek() {
    this.rowTargets.forEach((row) => {
      const value = row.getAttribute('value');
      if (!value) return;

      row.classList.toggle('same-week', dayjs(this.datetimeValue).isSame(dayjs(value), 'week'));
    });
  }

  private updateMonthPickerValue(value: Dayjs) {
    if (!this.monthPicker) return;
    this.monthPicker.setAttribute('data-skd-static-month-picker-datetime-value', value.startOf('month').format());
  }

  private populateCalendar() {
    this.monthGridTarget.innerHTML = '';

    chunks(buildMonthDays(this.activeDate), 7).forEach((week) => {
      const rowTemplate = this.rowTemplateTarget.content.cloneNode(true);
      const row = (rowTemplate as HTMLElement).querySelector('div');
      if (!row) return;

      row.setAttribute('value', week[0].format());
      week.forEach((day) => {
        const btnTemplate = this.btnTemplateTarget.content.cloneNode(true);
        const button = (btnTemplate as HTMLElement).querySelector('button');
        if (!button) return;

        button.classList.toggle('different-month', !this.activeDate.isSame(day, 'month'));
        row.classList.toggle('same-week', dayjs(this.datetimeValue).isSame(day, 'week'));

        button.innerText = day.format('D');
        row.append(button);
      });

      this.monthGridTarget.append(rowTemplate);
    });
  }

  private get monthPicker() {
    return this.element.querySelector<HTMLElement>('[data-controller^="skd-static-month-picker"]');
  }

  private get popover() {
    return this.element.closest<AwcPopoverElement>('awc-popover');
  }

  declare readonly monthTextTarget: HTMLElement;
  declare readonly monthGridTarget: HTMLElement;
  declare readonly rowTemplateTarget: HTMLTemplateElement;
  declare readonly btnTemplateTarget: HTMLTemplateElement;
  declare readonly rowTargets: HTMLElement[];
  declare datetimeValue: string;
}

function buildMonthDays(date: Dayjs) {
  const startDate = date.startOf('month').startOf('week');
  const endDate = date.endOf('month').endOf('week');
  let days = eachDayOfInterval({ start: startDate, end: endDate });

  // Make row height equal for all months
  for (let i = 1; days.length < 42; i += 7) {
    const nextEndDate = endDate.add(i, 'day');
    days = [...days, ...eachDayOfInterval({ start: nextEndDate, end: nextEndDate.endOf('week') })];
  }

  return days;
}
